// import hooks
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPageContext';
import { useGetDutyHourTypesTableData } from 'components/admin-portal/Tables/Settings/DutyHourTypes/hooks/useGetDutyHourTypesTableData';

// import types
import { DccFlag } from 'components/types';
import { TabProps } from '../CLASettings.types';

// import components
import withReadModeField from 'components/shared/CRUD/components/InnerPage/components/withReadModeField';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';
import ControlledDateRangePicker from 'components/shared/ControlledFields/ControlledDateRangePicker';
import {
  ControlledCheckbox,
  ControlledSelect,
  ControlledToggleButtonGroup
} from 'components/shared/ControlledFields';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import {
  StyledBoxFlexColumn,
  StyledFlexGrowBox
} from 'components/shared/Box/Box.styles';

// import utils
import { hoursPayoutSchema } from './HoursPayoutTab.schema';
import {
  PayOutReferenceMonth,
  PayOutThresholdType
} from './HoursPayoutTab.enums';
import {
  StyledLabelTypography,
  StyledFormRow,
  StyledFormRowWithGap,
  StyledThresholdValueBox,
  StyledToggleGroupWrapper
} from './HoursPayoutTab.styles';

const ControlledSelectWithReadMode = withReadModeField(ControlledSelect);
const ControlledNumericFieldWithReadMode = withReadModeField(
  ControlledNumericField
);

type PayoutSectionFieldsProps = {
  fieldNamePrefix: string;
  translationPrefix: string;
  filteredDhtOptions: { label: string; value: number }[];
  thresholdTypeButtons: { label: string; value: string }[];
  referenceMonthButtons?: { label: string; value: string }[];
};

const PayoutSectionFields = ({
  fieldNamePrefix,
  translationPrefix,
  filteredDhtOptions,
  thresholdTypeButtons,
  referenceMonthButtons
}: PayoutSectionFieldsProps) => {
  'use memo';

  const { t } = useTranslation('common', {
    keyPrefix: 'adminPortal.cla.hoursPayout'
  });
  const { isEditable } = useEditPageContext();
  const { control } = useFormContext();

  return (
    <StyledBoxFlexColumn>
      <StyledFormRow>
        <StyledLabelTypography variant="body1">
          {t(`${translationPrefix}.dutyHourType`)}
        </StyledLabelTypography>
        <StyledFlexGrowBox>
          <ControlledSelectWithReadMode
            control={control}
            variant="outlined"
            name={`${fieldNamePrefix}DhtId`}
            label=""
            options={filteredDhtOptions}
            valueGetter="value"
            labelGetter="label"
            hasClearButton
            displayEmpty
            fullWidth
          />
        </StyledFlexGrowBox>
      </StyledFormRow>

      <StyledFormRow>
        <StyledLabelTypography variant="body1">
          {t(`${translationPrefix}.threshold`)}
        </StyledLabelTypography>
        <StyledThresholdValueBox>
          <ControlledNumericFieldWithReadMode
            variant="outlined"
            name={`${fieldNamePrefix}Threshold`}
            label={t(`${translationPrefix}.thresholdValue`)}
          />
        </StyledThresholdValueBox>
        <StyledToggleGroupWrapper>
          <ControlledToggleButtonGroup
            control={control}
            name={`${fieldNamePrefix}ThresholdType`}
            label={t(`${translationPrefix}.thresholdType`)}
            disabled={!isEditable}
            inputToggleButtons={thresholdTypeButtons}
            direction="row"
          />
        </StyledToggleGroupWrapper>
      </StyledFormRow>

      <StyledFormRow>
        <StyledLabelTypography variant="body1">
          {t(`${translationPrefix}.period`)}
        </StyledLabelTypography>
        <ControlledDateRangePicker
          control={control}
          name={`${fieldNamePrefix}PeriodStart`}
          startName={`${fieldNamePrefix}PeriodStart`}
          endName={`${fieldNamePrefix}PeriodEnd`}
          label={t(`${translationPrefix}.period`)}
          startLabel={t(`${translationPrefix}.periodStart`)}
          endLabel={t(`${translationPrefix}.periodEnd`)}
          disabled={!isEditable}
        />
      </StyledFormRow>

      {referenceMonthButtons && (
        <StyledFormRowWithGap>
          <StyledLabelTypography variant="body1">
            {t(`${translationPrefix}.referenceMonth`)}
          </StyledLabelTypography>
          <StyledFlexGrowBox>
            <ControlledToggleButtonGroup
              control={control}
              name={`${fieldNamePrefix}ReferenceMonth`}
              label=""
              disabled={!isEditable}
              inputToggleButtons={referenceMonthButtons}
              direction="row"
            />
          </StyledFlexGrowBox>
        </StyledFormRowWithGap>
      )}
    </StyledBoxFlexColumn>
  );
};

const HoursPayoutTab = ({ setCurrentSchema }: TabProps) => {
  'use memo';

  const { t } = useTranslation('common', {
    keyPrefix: 'adminPortal.cla.hoursPayout'
  });
  const { isEditable } = useEditPageContext();
  const { control } = useFormContext();
  const overtimeEnabled = useWatch({ control, name: 'payOutOvertimeEnabled' });
  const extraLegalEnabled = useWatch({
    control,
    name: 'payOutExtraLegalEnabled'
  });
  const { data: dhtOptions } = useGetDutyHourTypesTableData();

  const filteredDhtOptions = dhtOptions
    ? [...dhtOptions]
        .filter(({ dcc }) => dcc?.flags?.includes(DccFlag.ALLOWED_WITH_NOMINAL))
        .sort((a, b) =>
          (a.description ?? '').localeCompare(b.description ?? '')
        )
        .map((item) => ({
          label: item.description ?? '',
          value: item.dutyTypeId
        }))
    : [];

  useEffect(() => {
    setCurrentSchema(hoursPayoutSchema);
  }, [setCurrentSchema]);

  const thresholdTypeButtons = [
    { label: t('thresholdType.HOURS'), value: PayOutThresholdType.HOURS },
    {
      label: t('thresholdType.PERCENTAGE'),
      value: PayOutThresholdType.PERCENTAGE
    }
  ];

  const referenceMonthButtons = [
    {
      label: t('referenceMonth.PREVIOUS_MONTH'),
      value: PayOutReferenceMonth.PREVIOUS_MONTH
    },
    {
      label: t('referenceMonth.CURRENT_MONTH'),
      value: PayOutReferenceMonth.CURRENT_MONTH
    }
  ];

  return (
    <>
      <MultiStepForm.Section title={t('overtime.title')}>
        <ControlledCheckbox
          control={control}
          disabled={!isEditable}
          name="payOutOvertimeEnabled"
          label={t('overtime.enabled')}
        />
        {overtimeEnabled && (
          <PayoutSectionFields
            fieldNamePrefix="payOutOvertime"
            translationPrefix="overtime"
            filteredDhtOptions={filteredDhtOptions}
            thresholdTypeButtons={thresholdTypeButtons}
            referenceMonthButtons={referenceMonthButtons}
          />
        )}
      </MultiStepForm.Section>

      <MultiStepForm.Section title={t('extraLegal.title')}>
        <ControlledCheckbox
          control={control}
          disabled={!isEditable}
          name="payOutExtraLegalEnabled"
          label={t('extraLegal.enabled')}
        />
        {extraLegalEnabled && (
          <PayoutSectionFields
            fieldNamePrefix="payOutExtraLegal"
            translationPrefix="extraLegal"
            filteredDhtOptions={filteredDhtOptions}
            thresholdTypeButtons={thresholdTypeButtons}
          />
        )}
      </MultiStepForm.Section>
    </>
  );
};

export default HoursPayoutTab;
